/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { useRequest } from 'ahooks';
import { userStoreService } from '@coze-studio/user-store';
import { usePageRuntimeStore } from '@coze-studio/bot-detail-store/page-runtime';
import { Toast } from '@coze-arch/coze-design';
import { OAuthStatus } from '@coze-arch/bot-api/plugin_develop';
import { ItemType } from '@coze-arch/bot-api/developer_api';
import { PluginDevelopApi } from '@coze-arch/bot-api';
import { preparePluginOAuthWindow } from '@coze-arch/bot-utils';

export const PLUGIN_PERMISSION_OPEN = 'plugin-permission-open';

export const openPluginPermissionManage = () =>
  window.dispatchEvent(new Event(PLUGIN_PERMISSION_OPEN));

export const usePluginPermissionManage = ({ botId }: { botId: string }) => {
  const userId = userStoreService.useUserInfo()?.user_id_str;
  const [busyPluginId, setBusyPluginId] = useState<string>();
  const {
    loading,
    error,
    data,
    runAsync: runGetList,
    mutate,
  } = useRequest(
    async () => {
      const result = await PluginDevelopApi.GetQueriedOAuthPluginList({
        bot_id: botId,
      });
      if (Number(result.code ?? 0) !== 0) {
        throw new Error('无法读取授权状态，请重试');
      }
      return result;
    },
    {
      ready: Boolean(botId && userId),
      refreshDeps: [botId, userId],
      cacheKey: `plugin-permission:${userId}:${botId}`,
      cacheTime: 60_000,
      refreshOnWindowFocus: true,
      focusTimespan: 0,
    },
  );

  useEffect(
    () =>
      usePageRuntimeStore.subscribe((state, previous) => {
        if (
          previous.savingInfo.saving &&
          !state.savingInfo.saving &&
          previous.savingInfo.scopeKey === String(ItemType.APIINFO)
        ) {
          void runGetList().catch(() => undefined);
        }
      }),
    [runGetList],
  );

  const runRevoke = async (pluginId: string) => {
    setBusyPluginId(pluginId);
    try {
      const result = await PluginDevelopApi.RevokeAuthToken({
        plugin_id: pluginId,
        bot_id: botId,
      });
      if (Number(result.BaseResp?.StatusCode ?? 0) !== 0) {
        throw new Error('取消授权失败，请重试');
      }
      mutate(previous => ({
        ...previous,
        oauth_plugin_list: previous?.oauth_plugin_list?.map(item =>
          item.plugin_id === pluginId
            ? {
                ...item,
                status: OAuthStatus.Unauthorized,
                oauth_url: undefined,
              }
            : item,
        ),
      }));
      Toast.success('已取消授权');
      // Revocation succeeded even if obtaining the next authorization URL fails.
      await runGetList().catch(() => undefined);
    } catch {
      Toast.error('取消授权失败，请重试');
    } finally {
      setBusyPluginId(undefined);
    }
  };

  const runAuthorize = async (pluginId: string) => {
    const popup = preparePluginOAuthWindow();
    if (!popup) {
      Toast.error('请允许浏览器打开授权窗口后重试');
      return;
    }
    setBusyPluginId(pluginId);
    try {
      const result = await runGetList();
      const plugin = result.oauth_plugin_list?.find(
        item => item.plugin_id === pluginId,
      );
      if (plugin?.status === OAuthStatus.Authorized) {
        popup.close();
        Toast.success('当前账号已授权');
        return;
      }
      if (
        !plugin?.oauth_url ||
        new URL(plugin.oauth_url).protocol !== 'https:'
      ) {
        throw new Error('authorization URL is unavailable');
      }
      popup.location.replace(plugin.oauth_url);
    } catch {
      popup.close();
      Toast.error('暂时无法发起授权，请重试');
    } finally {
      setBusyPluginId(undefined);
    }
  };

  const plugins = [
    ...new Map(
      (data?.oauth_plugin_list ?? []).map(item => [item.plugin_id, item]),
    ).values(),
  ];

  return {
    loading,
    error,
    data: plugins,
    unauthorizedCount: plugins.filter(
      item => item.status === OAuthStatus.Unauthorized,
    ).length,
    busyPluginId,
    runGetList,
    runRevoke,
    runAuthorize,
  };
};
